import type { Workbook, Worksheet } from 'exceljs';

import { formatInstant } from 'scenes/RouteReport/utils/time';

import {
  TiendaPerfectaVisitHistoryStop,
  TiendaPerfectaVisitHistorySummary,
} from 'models/TiendaPerfecta';

import { joinParts, zoneLabel } from './format';
import { DateRange } from './range';
import { AggregatedStops } from './selection';
import { VansMetrics, buildTotalRow, buildVanRows } from './vansTable';

/** Minimal translate function: the builder never depends on react-i18next. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface VisitHistoryExportInput {
  /** Already filtered by the active selection. */
  stops: TiendaPerfectaVisitHistoryStop[];
  summary: TiendaPerfectaVisitHistorySummary;
  /** `aggregateStops(stops, timeZone)` over the same filtered stops. */
  aggregated: AggregatedStops;
  range: DateRange;
  zoneNames: string[];
  distributorNames: string[];
  /** Human description of the active selection; null when there is none. */
  selectionLabel: string | null;
  userLabel: string;
  timeZone: string;
  generatedAt: Date;
  t: Translate;
}

const I18N = 'scenes.visit_history.export';
const UNNAMED_ZONE_KEY = 'scenes.visit_history.zone_unnamed';
const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';
const PERCENT_FORMAT = '0.0%';
const MIN_COLUMN_WIDTH = 8;
const MAX_COLUMN_WIDTH = 60;

type Cell = string | number | null;

interface Column {
  header: string;
  /** Minimum width in characters; the real one also follows the content. */
  width?: number;
  numFmt?: string;
}

export const visitHistoryFileName = (range: DateRange): string =>
  `visitas_camionetas_${range.from}_${range.to}.xlsx`;

const byDateRouteSequence = (
  a: TiendaPerfectaVisitHistoryStop,
  b: TiendaPerfectaVisitHistoryStop
): number =>
  a.plannedDate.localeCompare(b.plannedDate) ||
  a.route.code.localeCompare(b.route.code) ||
  a.sequence - b.sequence;

const materialTotal = (stop: TiendaPerfectaVisitHistoryStop): number =>
  stop.materials.reduce((sum, m) => sum + m.units, 0);

const textLength = (value: Cell): number =>
  value === null ? 0 : String(value).length;

/** Header row bold and frozen; widths follow the longest cell. */
const fillSheet = (
  sheet: Worksheet,
  columns: Column[],
  rows: Cell[][]
): void => {
  sheet.addRow(columns.map((c) => c.header));
  rows.forEach((row) => sheet.addRow(row));

  sheet.getRow(1).font = { bold: true };
  sheet.views = [{ state: 'frozen', ySplit: 1 }];

  columns.forEach((column, index) => {
    const longest = rows.reduce(
      (max, row) => Math.max(max, textLength(row[index])),
      column.header.length
    );
    const target = sheet.getColumn(index + 1);
    target.width = Math.min(
      MAX_COLUMN_WIDTH,
      Math.max(column.width ?? MIN_COLUMN_WIDTH, longest + 2)
    );
  });

  // The format goes on the body cells only: a column format would also reach
  // the header row.
  columns.forEach((column, index) => {
    const { numFmt } = column;
    if (!numFmt) return;
    for (let r = 2; r <= rows.length + 1; r += 1) {
      sheet.getRow(r).getCell(index + 1).numFmt = numFmt;
    }
  });
};

const detailSheet = (
  workbook: Workbook,
  input: VisitHistoryExportInput,
  stops: TiendaPerfectaVisitHistoryStop[]
): void => {
  const { t, timeZone } = input;
  const c = (key: string) => t(`${I18N}.columns.detail.${key}`);
  const columns: Column[] = [
    { header: c('date') },
    { header: c('zone') },
    { header: c('distributor') },
    { header: c('van') },
    { header: c('route') },
    { header: c('order') },
    { header: c('store') },
    { header: c('branch_id') },
    { header: c('address') },
    { header: c('lat') },
    { header: c('lng') },
    { header: c('status') },
    { header: c('incident_reason') },
    { header: c('started') },
    { header: c('finished') },
    { header: c('material_total') },
  ];
  const rows = stops.map((stop): Cell[] => [
    stop.plannedDate,
    zoneLabel(stop.zone, t(UNNAMED_ZONE_KEY)),
    stop.distributor.tradeName,
    stop.van.name,
    stop.route.code,
    stop.sequence,
    stop.store.label ?? stop.store.name ?? stop.store.code,
    stop.store.branchExternalId,
    stop.store.address,
    stop.location?.lat ?? null,
    stop.location?.lng ?? null,
    t(`${I18N}.status.${stop.outcome}`),
    stop.incident?.reason ?? null,
    stop.startedAt
      ? formatInstant(stop.startedAt, timeZone, DATE_TIME_FORMAT)
      : null,
    stop.finishedAt
      ? formatInstant(stop.finishedAt, timeZone, DATE_TIME_FORMAT)
      : null,
    materialTotal(stop),
  ]);

  fillSheet(workbook.addWorksheet(t(`${I18N}.sheets.detail`)), columns, rows);
};

const materialSheet = (
  workbook: Workbook,
  input: VisitHistoryExportInput,
  stops: TiendaPerfectaVisitHistoryStop[]
): void => {
  const { t } = input;
  const c = (key: string) => t(`${I18N}.columns.material.${key}`);
  const columns: Column[] = [
    { header: c('date') },
    { header: c('route') },
    { header: c('order') },
    { header: c('branch_id') },
    { header: c('material') },
    { header: c('sku') },
    { header: c('quantity') },
  ];
  const rows = stops.flatMap((stop) =>
    stop.materials.map((m): Cell[] => [
      stop.plannedDate,
      stop.route.code,
      stop.sequence,
      stop.store.branchExternalId,
      m.name,
      m.sku ?? null,
      m.units,
    ])
  );

  fillSheet(workbook.addWorksheet(t(`${I18N}.sheets.material`)), columns, rows);
};

/** A share in [0, 100] stored as a fraction, so the cell format shows `%`. */
const fraction = (percent: number | null): number | null =>
  percent === null ? null : percent / 100;

const summarySheet = (
  workbook: Workbook,
  input: VisitHistoryExportInput
): void => {
  const { t, summary, aggregated, timeZone } = input;
  const c = (key: string) => t(`${I18N}.columns.summary.${key}`);
  const columns: Column[] = [
    { header: c('van'), width: 28 },
    { header: c('zones') },
    { header: c('routes') },
    { header: c('days') },
    { header: c('planned') },
    { header: c('executed') },
    { header: c('incidents') },
    { header: c('incident_pct'), numFmt: PERCENT_FORMAT },
    { header: c('pending') },
    { header: c('compliance'), numFmt: PERCENT_FORMAT },
    { header: c('material') },
    { header: c('avg_start') },
    { header: c('avg_end') },
    { header: c('avg_duration') },
    { header: c('visits_per_hour') },
  ];

  const zoneNames = (zones: { id: string; name: string }[]): string =>
    joinParts(
      zones.map((z) => zoneLabel(z, t(UNNAMED_ZONE_KEY))),
      ', '
    );

  const vanRows = buildVanRows(summary, aggregated, new Map(), timeZone);
  const total = buildTotalRow(aggregated);
  const metrics = (m: VansMetrics): Cell[] => [
    m.planned,
    m.executed,
    m.incidents,
    fraction(m.incidentPct),
    m.pending,
    fraction(m.compliancePct),
    m.materialUnits,
    m.avgStart,
    m.avgEnd,
    m.avgDurationHours,
    m.visitsPerHour,
  ];

  const rows: Cell[][] = [];
  const executionRows = new Set<number>();
  vanRows.forEach((van) => {
    rows.push([
      van.name,
      zoneNames(van.zones),
      van.routes,
      van.days,
      ...metrics(van),
    ]);
    van.children.forEach((child) => {
      executionRows.add(rows.length + 2);
      rows.push([
        joinParts([child.date, child.name]),
        zoneNames(child.zones),
        child.routes,
        child.days,
        ...metrics(child),
      ]);
    });
  });
  rows.push([
    t(`${I18N}.summary_total`),
    total.zonesCount,
    total.routes,
    total.days,
    ...metrics(total),
  ]);

  const sheet = workbook.addWorksheet(t(`${I18N}.sheets.summary`));
  fillSheet(sheet, columns, rows);

  executionRows.forEach((r) => {
    sheet.getRow(r).getCell(1).alignment = { indent: 2 };
  });
  sheet.getRow(rows.length + 1).font = { bold: true };
};

const filtersSheet = (
  workbook: Workbook,
  input: VisitHistoryExportInput
): void => {
  const { t, range, timeZone } = input;
  const label = (key: string) => t(`${I18N}.filters.${key}`);
  const list = (names: string[], allKey: string): string =>
    names.length ? names.join(', ') : label(allKey);

  const rows: Cell[][] = [
    [
      label('range'),
      t(`${I18N}.filters.range_value`, { from: range.from, to: range.to }),
    ],
    [label('zones'), list(input.zoneNames, 'all_zones')],
    [label('distributors'), list(input.distributorNames, 'all_distributors')],
    [label('selection'), input.selectionLabel ?? label('no_selection')],
    [label('user'), input.userLabel],
    [
      label('generated_at'),
      formatInstant(input.generatedAt, timeZone, DATE_TIME_FORMAT),
    ],
  ];

  const sheet = workbook.addWorksheet(t(`${I18N}.sheets.filters`));
  rows.forEach((row) => sheet.addRow(row));
  sheet.getColumn(1).font = { bold: true };
  sheet.getColumn(1).width = 24;
  sheet.getColumn(2).width = Math.min(
    MAX_COLUMN_WIDTH,
    rows.reduce((max, row) => Math.max(max, textLength(row[1]) + 2), 24)
  );
};

/**
 * Builds the four-sheet workbook from the filtered stops. `exceljs` is loaded
 * on demand so it stays out of the initial bundle.
 */
export const buildVisitHistoryWorkbook = async (
  input: VisitHistoryExportInput
): Promise<Workbook> => {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const stops = [...input.stops].sort(byDateRouteSequence);

  detailSheet(workbook, input, stops);
  materialSheet(workbook, input, stops);
  summarySheet(workbook, input);
  filtersSheet(workbook, input);

  return workbook;
};

export const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** The workbook as a downloadable Blob. */
export const workbookToBlob = async (workbook: Workbook): Promise<Blob> =>
  new Blob([await workbook.xlsx.writeBuffer()], { type: XLSX_MIME });
